import { Input, Select, Typography } from "antd";
import {
  IInteractiveModCalendar,
  interactiveCalendarApps,
  interactiveCalendarTimeZoneModes,
} from "@blings/blings-player";
import { useEffect } from "react";
import MainCTAButton from "../../../MainCTA/MainCTAButton";
import {
  CALENDAR_APP_OPTIONS,
  CALENDAR_TIME_ZONE_MODE_OPTIONS,
  getActiveCalendarSource,
  getCalendarProviderHelp,
  getSupportedCalendarTimeZones,
  parseCalendarEventValue,
} from "../../calendarConnector";
import { ModsPanelCompProps } from "../../ModsPanel";
import { CalendarSourceEditor } from "./CalendarSourceEditor";
import { AddInteractiveHOC } from "./InteractiveFormHOC";
import { useInteractiveCtaNameAutofill } from "./useInteractiveCtaNameAutofill";

const CALENDAR_TIME_ZONES = getSupportedCalendarTimeZones();

const renderCalendarForm = (
  props: ModsPanelCompProps<IInteractiveModCalendar>
) => {
  const { change, index, onChange } = props;
  const providerHelp = getCalendarProviderHelp(change.app);
  const source = getActiveCalendarSource(change);
  const staticEvent = parseCalendarEventValue(change.value);
  const staticAllDay =
    source === "value" && staticEvent && staticEvent.allDay === true;

  const handleTimeZoneModeChange = (
    timeZoneMode: interactiveCalendarTimeZoneModes
  ) =>
    onChange(
      index,
      ["timeZoneMode", "timeZone"],
      [
        timeZoneMode,
        timeZoneMode === interactiveCalendarTimeZoneModes.viewer
          ? undefined
          : change.timeZone,
      ]
    );

  return (
    <>
      <label>
        <span className="calendar-field-label">
          Name this button (Required for analytics)
        </span>
        <Input
          size="large"
          id="connector-cta-name"
          value={change.ctaName}
          placeholder="Name this button"
          onChange={(event) => onChange(index, "ctaName", event.target.value)}
          allowClear
        />
      </label>

      <label>
        <span className="calendar-field-label">Open with</span>
        <Select
          size="large"
          style={{ width: "100%" }}
          value={change.app}
          onChange={(app) => onChange(index, "app", app)}
          options={CALENDAR_APP_OPTIONS}
        />
        {providerHelp && (
          <Typography.Text type="secondary" className="calendar-field-help">
            {providerHelp}
          </Typography.Text>
        )}
      </label>

      <CalendarSourceEditor change={change} index={index} onChange={onChange} />

      {!staticAllDay && (
        <div className="calendar-timezone-fields">
          <label>
            <span className="calendar-field-label">Timezone behavior</span>
            <Select
              size="large"
              style={{ width: "100%" }}
              value={change.timeZoneMode}
              onChange={handleTimeZoneModeChange}
              options={CALENDAR_TIME_ZONE_MODE_OPTIONS}
            />
          </label>

          {change.timeZoneMode === interactiveCalendarTimeZoneModes.fixed && (
            <label>
              <span className="calendar-field-label">Specific timezone *</span>
              <Select
                size="large"
                showSearch
                style={{ width: "100%" }}
                value={change.timeZone}
                placeholder="Search IANA timezones"
                optionFilterProp="label"
                onChange={(timeZone) => onChange(index, "timeZone", timeZone)}
                options={CALENDAR_TIME_ZONES.map((timeZone) => ({
                  value: timeZone,
                  label: timeZone,
                }))}
              />
            </label>
          )}
        </div>
      )}

      <MainCTAButton change={change} onChange={onChange} index={index} />
    </>
  );
};

export const AddInteractiveCalendarForm = (
  props: ModsPanelCompProps<IInteractiveModCalendar>
) => {
  const { change, index, onChange } = props;
  useInteractiveCtaNameAutofill(props);
  const source = getActiveCalendarSource(change);
  const staticEvent = parseCalendarEventValue(change.value);
  const staticAllDay =
    source === "value" && staticEvent && staticEvent.allDay === true;

  useEffect(() => {
    const keys: Array<keyof IInteractiveModCalendar> = [];
    const values: any[] = [];
    if (!change.app) {
      keys.push("app");
      values.push(interactiveCalendarApps.system);
    }
    if (!change.timeZoneMode) {
      keys.push("timeZoneMode");
      values.push(interactiveCalendarTimeZoneModes.viewer);
    }
    if (keys.length) onChange(index, keys, values);
  }, [change.app, change.timeZoneMode, index, onChange]);

  useEffect(() => {
    if (
      staticAllDay &&
      (change.timeZoneMode !== interactiveCalendarTimeZoneModes.viewer ||
        change.timeZone)
    ) {
      onChange(
        index,
        ["timeZoneMode", "timeZone"],
        [interactiveCalendarTimeZoneModes.viewer, undefined]
      );
    }
  }, [change.timeZone, change.timeZoneMode, index, onChange, staticAllDay]);

  return (
    <AddInteractiveHOC<IInteractiveModCalendar>
      {...props}
      renderSpecificInteractive={renderCalendarForm}
    />
  );
};
